/**
 * Zero Human brand tokens, from docs/brand-guidelines.md — cited throughout as BG, to tell it
 * apart from the landing page spec (LP), whose numbering collides. Here: BG §18–19.
 *
 * These values are duplicated as custom properties in `theme.css`, because CSS cannot import from
 * TypeScript and a build step to generate one from the other would be a build step to maintain.
 * `tokens.spec.ts` parses the stylesheet and fails if the two ever disagree, which is what makes the
 * duplication safe. Anything that needs a brand value in JavaScript — an OG image, a canvas, an
 * inline SVG stop — reads it from here rather than hard-coding a hex.
 */

/** BG §18.1. The page base and the two signal colours the brand is built on. */
export const PRIMARY_PALETTE = {
    /** Main background, page base. */
    voidBlack: '#050505',
    /** Panels, cards, UI surfaces. */
    systemCharcoal: '#111315',
    /** Primary text, logo on dark. */
    dirtyWhite: '#E6E0D6',
    /** Primary accent, CTA, command signal. */
    neonMagenta: '#FF006E',
    /** System status, links, data lines. */
    electricCyan: '#00CFFF',
} as const

/** BG §18.2. Run and gate states, plus the environmental warmth that keeps the palette off pure grey. */
export const SECONDARY_PALETTE = {
    /** Work in flight: running runs and executions. */
    warningAmber: '#FFB000',
    /** Completed runs, approved states. */
    successGreen: '#39FF88',
    /** Texture, shadows, environmental warmth. */
    rustBrick: '#5A241F',
    /** Queued, paused and otherwise inactive states. */
    mutedGrey: '#7B7B7B',
} as const

export const PALETTE = { ...PRIMARY_PALETTE, ...SECONDARY_PALETTE } as const

export type PaletteToken = keyof typeof PALETTE

/**
 * BG §18.3 / BG §18.4. What a colour *means*, so a status is never picked by eye. This is the whole status
 * colour system in one object: every badge, pill, dot, graph node, chart edge and sidebar count in
 * the product resolves to one of these five states, and nothing may invent a sixth.
 *
 *   green  — it finished, and it finished well
 *   amber  — it is moving right now
 *   grey   — it is not moving, and nothing is being asked of you
 *   pink   — it ended badly, or it cannot proceed
 *   blue   — it is waiting on a human
 *
 * `system` is deliberately the same cyan as `action`: it is not a run state at all, but the channel
 * the console uses to talk about itself (build number, links, data lines). BG §22.3 is what keeps the two
 * apart — every status also carries a glyph and a word, so the colour is never the only signal.
 */
export const STATE_COLOUR = {
    /** Running, in progress, processing. */
    active: PALETTE.warningAmber,
    /** Succeeded, approved, accepted, healthy. */
    complete: PALETTE.successGreen,
    /** Queued, pending, paused, cancelled, skipped. */
    inactive: PALETTE.mutedGrey,
    /** Failed, blocked, rejected. */
    failed: PALETTE.neonMagenta,
    /** Action required: a gate, an approval, a decision only the human can make. */
    action: PALETTE.electricCyan,
    /** Not a run state: the console itself — build, links, data lines. */
    system: PALETTE.electricCyan,
} as const

export type ExecutionState = keyof typeof STATE_COLOUR

/**
 * The tone vocabulary every status badge in the product speaks. Five of them are the BG §18.4 states;
 * `system` and `plan` are chrome that happens to be shaped like a badge.
 */
export const STATUS_TONES = ['running', 'completed', 'neutral', 'failed', 'action', 'system', 'plan'] as const

export type StatusTone = (typeof STATUS_TONES)[number]

/** BG §19. Headline is Anton per the brand decision; mono carries all system metadata. */
// Double quotes inside the stack, because that is how Biome formats CSS and tokens.spec.ts compares
// these strings to theme.css character for character.
export const FONT_FAMILY = {
    display: '"Anton", "Arial Narrow", sans-serif',
    body: '"Space Grotesk", "Inter", system-ui, sans-serif',
    mono: '"IBM Plex Mono", "SFMono-Regular", ui-monospace, monospace',
} as const

/**
 * BG §22.2. Motion should read as a live system rather than a marketing animation, so the timings are
 * deliberately slow and shared: everything that pulses on the page pulses on one of these.
 */
export const MOTION = {
    /** BG §22.4 — the status dot. Slow enough to never read as a flash. */
    statusBlinkMs: 2200,
    /** BG §20.2 — the execution map's active run. */
    runPulseMs: 3200,
    /** Logo glitch: how long one glitch lasts, and how often it fires. */
    glitchMs: 120,
    glitchIntervalMs: 9000,
    /** UI feedback on buttons and inputs. */
    uiMs: 140,
    /** A decision just made (ZhDecisionCard `decided`): the card shows it, then has faded, in this long. */
    decisionMs: 700,
} as const

/**
 * Ink and surface steps (theme.css `--ink-*`, `--line*`, `--hair`, `--panel*`): what text, rules, and panels
 * are drawn in on a dark ground. Mirrored here so tokens.spec.ts fails if the two drift.
 */
export const SURFACE = {
    ink1: 'var(--dirty-white)',
    ink2: 'color-mix(in srgb, var(--dirty-white) 80%, var(--void-black))',
    ink3: 'color-mix(in srgb, var(--dirty-white) 60%, var(--void-black))',
    line: 'color-mix(in srgb, var(--dirty-white) 45%, var(--void-black))',
    lineStrong: 'color-mix(in srgb, var(--dirty-white) 70%, var(--void-black))',
    hair: 'color-mix(in srgb, var(--dirty-white) 14%, var(--void-black))',
    panel: 'var(--system-charcoal)',
    panel2: 'color-mix(in srgb, var(--dirty-white) 4%, var(--system-charcoal))',
} as const

/** Brand colours as an inline `style` object, for the rare place a CSS variable cannot reach. */
export function paletteStyle(): Record<string, string> {
    return Object.fromEntries(Object.entries(PALETTE).map(([name, value]) => [`--${kebab(name)}`, value]))
}

/** `neonMagenta` → `neon-magenta`, matching the custom-property names in `theme.css`. */
export function kebab(name: string): string {
    return name.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`)
}

/*
 * The v2 size and rhythm: the type scale, the section spacing and the layout tokens, copied exactly from the
 * v2 home's `.home3` block (apps/www/src/components/site3/site3.css). They are CSS expressions rather than
 * numbers, because most of them are responsive (`clamp`, `max`) or keyed to another token (`var(--h-hero)`),
 * so they only resolve in a browser. theme.css declares the same strings at `:root`, and
 * tokens-scale.spec.ts compares the two character for character. Frozen, so nothing at runtime can
 * re-tune one copy.
 */

/**
 * The v2 type scale (theme.css `--h-*` and `--prose`). Keyed to the hero, so the hero heading is always the
 * largest type on the page: lead and exit are section headings a touch under it, figure and cell cap
 * display type inside figures and cells, and prose is body copy.
 */
export const TYPE_SCALE = Object.freeze({
    hero: 'clamp(1.9rem, 6.2vw, 3.5rem)',
    lead: 'max(1.6rem, calc(var(--h-hero) * 0.92))',
    exit: 'max(1.4rem, calc(var(--h-hero) * 0.72))',
    figure: 'calc(var(--h-lead) * 0.9)',
    cell: 'calc(var(--h-exit) * 0.9)',
    prose: 'clamp(1.0625rem, 1rem + 0.25vw, 1.1875rem)',
} as const)

export type TypeScaleToken = keyof typeof TYPE_SCALE

/** The v2 section rhythm (theme.css `--space-*`): before the first section, between sections, before the exit. */
export const SPACING = Object.freeze({
    first: 'clamp(4rem, 2rem + 5vw, 7rem)',
    lead: 'clamp(6rem, 3rem + 7vw, 10rem)',
    exit: 'clamp(4rem, 2.5rem + 4vw, 7rem)',
} as const)

export type SpacingToken = keyof typeof SPACING

/**
 * The v2 layout tokens (theme.css `--page-max`, `--gutter`, `--col-gap`, `--bar-h`): the page width, the
 * responsive gutter either side of it, the gap between columns, and the fixed top chrome's height.
 * `barHNarrow` is the `--bar-h` override theme.css applies below 48rem, so it has no custom property of its own.
 */
export const LAYOUT = Object.freeze({
    pageMax: '80rem',
    gutter: 'clamp(1rem, 4vw, 3rem)',
    colGap: 'clamp(1rem, 2vw, 1.5rem)',
    barH: '4.5rem',
    barHNarrow: '3.5rem',
} as const)

export type LayoutToken = keyof typeof LAYOUT
